import React from 'react';

// Read-only display renderer (plan §4.6). Interprets a blueprint's
// display_schema.json + an artifact into UI. Supported node set (kept small on
// purpose): root, section, field, array (ul|cards|tabs|table), text-block.
// Unknown node types render a placeholder. Tabs/cards render as stacked titled
// cards in v0 (no tab state). valueTypes: string|number|score|importance|badge.

const IMPORTANCE = {
    critical: { bg: '#fef2f2', text: '#dc2626' },
    high: { bg: '#fffbeb', text: '#d97706' },
    medium: { bg: '#eff6ff', text: '#2563eb' },
    low: { bg: '#f0fdf4', text: '#16a34a' },
};

function ScoreBadge({ value }) {
    const n = Number(value);
    const color = n >= 8 ? '#10b981' : n >= 6 ? '#f59e0b' : n >= 4 ? '#f97316' : '#ef4444';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 26, height: 26, padding: '0 6px', borderRadius: 13,
            background: color, color: '#fff', fontWeight: 700, fontSize: 12,
        }}>{value}</span>
    );
}

function Pill({ level }) {
    const c = IMPORTANCE[String(level)] || IMPORTANCE.medium;
    return (
        <span style={{
            padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', background: c.bg, color: c.text,
        }}>{String(level)}</span>
    );
}

function formatValue(node, value) {
    if (value === null || value === undefined || value === '') {
        return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;
    }
    switch (node.valueType) {
        case 'score':
            return <ScoreBadge value={value} />;
        case 'importance':
        case 'badge':
            return <Pill level={value} />;
        case 'number':
            return <span>{String(value)}</span>;
        default:
            return <span style={{ whiteSpace: 'pre-wrap' }}>{String(value)}</span>;
    }
}

function Field({ node, data }) {
    const value = node.key === '' || node.key == null ? data : (data ? data[node.key] : undefined);
    const label = node.label || (node.key ? prettify(node.key) : null);
    return (
        <div style={{ marginBottom: 8 }}>
            {label && (
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    {label}
                </div>
            )}
            <div style={{ fontSize: 14, color: node.bold ? '#111827' : '#374151', fontWeight: node.bold ? 700 : 400, lineHeight: 1.5 }}>
                {formatValue(node, value)}
            </div>
        </div>
    );
}

function Section({ node, data }) {
    const sub = (data && node.key) ? data[node.key] : data;
    return (
        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 14, background: '#fff' }}>
            {node.title && (
                <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#083A54' }}>{node.title}</h3>
            )}
            {(node.children || []).map((child, i) => <Node key={i} node={child} data={sub} />)}
        </section>
    );
}

function ArrayNode({ node, data }) {
    const arr = node.key === '' || node.key == null ? data : (data ? data[node.key] : undefined);
    if (!Array.isArray(arr)) return null;
    const display = node.display || 'ul';
    const children = node.children || [];
    const scalarChild = children.length === 1 && (children[0].key === '' || children[0].key == null);

    if (display === 'ul') {
        return (
            <ul style={{ margin: '4px 0 12px', paddingLeft: 18 }}>
                {arr.map((item, i) => (
                    <li key={i} style={{ fontSize: 14, color: '#374151', marginBottom: 3 }}>
                        {scalarChild ? String(item) : <InlineFields children={children} data={item} />}
                    </li>
                ))}
            </ul>
        );
    }

    if (display === 'table') {
        const cols = children.filter(c => c.type === 'field');
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 13 }}>
                <thead>
                    <tr>{cols.map((c, i) => (
                        <th key={i} style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb', padding: '6px 8px', color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>
                            {c.label || prettify(c.key)}
                        </th>
                    ))}</tr>
                </thead>
                <tbody>
                    {arr.map((row, ri) => (
                        <tr key={ri}>{cols.map((c, ci) => (
                            <td key={ci} style={{ borderBottom: '1px solid #f3f4f6', padding: '6px 8px', verticalAlign: 'top' }}>
                                {formatValue(c, row ? row[c.key] : undefined)}
                            </td>
                        ))}</tr>
                    ))}
                </tbody>
            </table>
        );
    }

    // cards / tabs -> stacked titled cards (v0)
    const [titleChild, ...rest] = children;
    return (
        <div style={{ marginBottom: 12 }}>
            {arr.map((item, i) => (
                <div key={i} style={{ border: '1px solid #e5e7eb', borderLeft: '4px solid #083A54', borderRadius: 8, padding: '10px 14px', marginBottom: 10, background: '#fff' }}>
                    {titleChild && (
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#083A54', marginBottom: 8 }}>
                            {item ? (titleChild.key ? item[titleChild.key] : item) : ''}
                        </div>
                    )}
                    {rest.map((child, ci) => <Node key={ci} node={child} data={item} />)}
                </div>
            ))}
        </div>
    );
}

function InlineFields({ children, data }) {
    return <>{children.map((c, i) => <Node key={i} node={c} data={data} />)}</>;
}

function prettify(key) {
    return String(key).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function Node({ node, data }) {
    if (!node) return null;
    switch (node.type) {
        case 'root':
            return <>{(node.children || []).map((c, i) => <Node key={i} node={c} data={data} />)}</>;
        case 'section':
            return <Section node={node} data={data} />;
        case 'array':
            return <ArrayNode node={node} data={data} />;
        case 'field':
            return <Field node={node} data={data} />;
        case 'text-block':
            return <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{node.text || (data && node.key ? data[node.key] : '')}</p>;
        case 'divider':
            return <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />;
        default:
            return <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }}>[unsupported display node: {node.type}]</div>;
    }
}
